/*
 * The Frosted Muzzle — micro-interactions (Phase 6 / Interactive).
 *
 * Two IntersectionObserver-driven effects, both reduced-motion safe:
 *   1. Scroll-reveal — elements marked [data-fm-reveal] fade/rise in when they
 *      enter the viewport. Optional [data-fm-reveal-order] staggers siblings.
 *   2. Count-up — elements marked [data-fm-count-up] animate their leading
 *      number from 0 to the authored value, preserving any surrounding text.
 *
 * When the user prefers reduced motion (or IntersectionObserver is missing),
 * revealed elements are shown immediately and count-up text is left exactly as
 * authored — no motion at all. The initial "hidden" state lives only inside a
 * prefers-reduced-motion: no-preference media query in reveal.css, so reduced
 * motion never leaves anything invisible.
 */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function revealNow(el) {
    el.setAttribute('data-fm-revealed', 'true');
  }

  function setupReveal() {
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-fm-reveal]:not([data-fm-revealed])'));
    if (!els.length) return;

    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(revealNow);
      return;
    }

    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var order = parseInt(el.getAttribute('data-fm-reveal-order') || '0', 10);
          if (order) el.style.setProperty('--fm-reveal-delay', order * 80 + 'ms');
          revealNow(el);
          obs.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );
    els.forEach(function (el) {
      io.observe(el);
    });
  }

  function parseNumber(text) {
    var m = String(text).match(/\d[\d,]*(\.\d+)?/);
    if (!m) return null;
    return { raw: m[0], value: parseFloat(m[0].replace(/,/g, '')), index: m.index };
  }

  function format(n, decimals) {
    return n.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  function countUp(el) {
    var original = el.getAttribute('data-fm-count-original');
    if (original === null) {
      original = el.textContent;
      el.setAttribute('data-fm-count-original', original);
    }
    var parsed = parseNumber(original);
    if (!parsed) return;

    // Reduced motion / no observer: never mutate — leave the authored value.
    if (reduce) return;

    var decimals = (parsed.raw.split('.')[1] || '').length;
    var target = parsed.value;
    var duration = 1200;
    var startTs = null;

    function frame(ts) {
      if (startTs === null) startTs = ts;
      var progress = Math.min((ts - startTs) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = target * eased;
      var num = decimals ? format(current, decimals) : format(Math.round(current), 0);
      el.textContent = original.slice(0, parsed.index) + num + original.slice(parsed.index + parsed.raw.length);
      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        el.textContent = original; // restore the exact authored string
      }
    }
    requestAnimationFrame(frame);
  }

  function setupCountUp() {
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-fm-count-up]'));
    if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          countUp(entry.target);
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.5 }
    );
    els.forEach(function (el) {
      io.observe(el);
    });
  }

  function init() {
    setupReveal();
    setupCountUp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-scan when a section is added/edited in the theme editor.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
